import { CheckIcon, CircleCheckIcon, CircleSubtractIcon, EquipmentIcon, UserChangedIcon } from "@xola/icons";
import { Dot, Popover, Skeleton } from "@xola/ui-kit";
import { RowNode } from "ag-grid-community";
import { useMount } from "ahooks";
import clsx from "clsx";
import { isEquipmentItem, PurchaseItemStatus, SecondaryButton } from "common";
import { sum } from "lodash";
import React, { useState } from "react";
import { ExperiencePurchaseItem, GuestStatus, PurchaseItemObject } from "types";
import {
    getCheckInStatusInfo,
    GuestSummaryByStatus,
} from "../../../PurchaseModification/CheckInPurchaseGuests/CheckInHelper";
import { RosterGuestBookingStatus } from "../RosterGuestBookingStatus";
import { RowData } from "./types";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { CheckInEquipmentUnitMenu } from "@/components/PurchaseModification/CheckInPurchaseGuests/CheckInEquipmentUnitMenu";
import { CheckInPurchaseGuestMenu } from "@/components/PurchaseModification/CheckInPurchaseGuests/CheckInForRosterGuestsList";

export const checkInStatusValueGetter = ({ data }: { data: RowData }) => {
    if ("checkInStatus" in data) {
        return data.checkInStatus;
    }

    if (data.equipment) {
        return data.equipment.checkInStatus;
    }

    if (data.rosterSection === "equipment" && isEquipmentItem(data.item)) {
        return (data.item.equipments ?? []).map((unit) => unit.checkInStatus).join(",");
    }

    const isExperienceOrEquipment = data.item.object === PurchaseItemObject.EXPERIENCE || isEquipmentItem(data.item);
    if (isExperienceOrEquipment && data.item.status === PurchaseItemStatus.CONFIRMED) {
        const { isCheckedIn, isNoShow } = getCheckInStatusInfo(data.item as ExperiencePurchaseItem, data.guest);

        return isCheckedIn ? 0 : isNoShow ? 1 : 2;
    }

    return 0;
};

export const CheckInStatusCellRenderer = ({ data, node }: { data: RowData; node: RowNode }) => {
    if (!node.expanded && node.key === "summary" && "checkInStatus" in data) {
        const checkInCountValues = Object.values(data.checkInStatus);
        const totalCount = sum(checkInCountValues);
        if (totalCount === 0) {
            return null;
        }
        return (
            <Popover appendTo={document.body}>
                <CheckInStatus isPopover={false} isExpanded={node.expanded} checkInStatus={data.checkInStatus} />
                <Popover.Content>
                    <div className="w-auto min-w-[120px] p-3">
                        <CheckInStatusPopoverSummary groupedGuestsData={data.groupedGuestsData} />
                    </div>
                </Popover.Content>
            </Popover>
        );
    }

    if ("checkInStatus" in data) {
        // This is when you are given an event level breakdown of the check in status by arrived, no-show etc
        // It follows the SummaryData interface
        return (
            <Popover appendTo={document.body}>
                <CheckInStatus isPopover={false} isExpanded={node.expanded} checkInStatus={data.checkInStatus} />
                <Popover.Content>
                    <div className="w-auto min-w-[120px] p-3">
                        <CheckInStatusPopoverSummary groupedGuestsData={data.groupedGuestsData} />
                    </div>
                </Popover.Content>
            </Popover>
        );
    }

    const isExperienceOrEquipment = data.item.object === PurchaseItemObject.EXPERIENCE || isEquipmentItem(data.item);
    if (isExperienceOrEquipment) {
        if ("isCheckInAllowed" in data && !data.isCheckInAllowed) {
            return null;
        }

        // This is the check-in status for every purchase
        const isBookingConfirmed = data.item.status === PurchaseItemStatus.CONFIRMED;
        return isBookingConfirmed ? (
            <DeferredCheckInPurchaseGuestMenu data={data} />
        ) : (
            <RosterGuestBookingStatus
                purchase={data.item.purchase}
                purchaseItem={data.item as ExperiencePurchaseItem}
            />
        );
    }

    return null;
};

export const DeferredCheckInPurchaseGuestMenu = ({ data }: { data: RowData }) => {
    const [show, setShow] = useState(false);

    useMount(() => {
        const id = setTimeout(() => setShow(true), 100);
        return () => clearTimeout(id);
    });

    if (!show) return <CheckInButtonSkeleton />;

    if (!("item" in data)) {
        return null;
    }

    const purchase = structuredClone(data.item.purchase);
    const itemIndex = purchase.items.findIndex((item) => item.id === data.item.id);
    purchase.items[itemIndex] = data.item;

    if (data.equipment || data.rosterSection === "equipment") {
        return (
            <CheckInEquipmentUnitMenu
                purchase={purchase}
                purchaseItem={data.item as ExperiencePurchaseItem}
                equipment={data.equipment}
            />
        );
    }

    // "Attendees" sub-summary row has no check-in action of its own.
    if (data.rosterSection === "attendees") {
        return null;
    }

    return (
        <CheckInPurchaseGuestMenu
            isOrganizer={data.isOrganizer ?? false}
            purchase={purchase}
            purchaseItem={data.item as ExperiencePurchaseItem}
            guest={data.guest}
        />
    );
};

const CheckInButtonSkeleton = () => (
    <Skeleton className="bg-gray-200 rounded">
        <SecondaryButton size="small" color="success" className="mr-2 mt-1">
            <CircleCheckIcon />
        </SecondaryButton>
        <SecondaryButton size="small" color="danger">
            <CircleSubtractIcon />
        </SecondaryButton>
    </Skeleton>
);

export const CheckInStatus = ({
    checkInStatus,
    isExpanded = false,
    isPopover = false,
}: {
    checkInStatus: { [key in GuestStatus]: number };
    isExpanded?: boolean;
    isPopover?: boolean;
}) => {
    if (Object.values(checkInStatus).every((value) => value === 0)) {
        return null;
    }
    return (
        <ul
            className={clsx(
                !isExpanded && !isPopover && "flex w-fit items-center space-x-2 border-b border-dotted",
                !isPopover && "pt-2",
                "leading-5",
            )}
        >
            <li
                className={clsx("flex items-center space-x-1", isPopover && "justify-between")}
                data-testid="check-in-status-checked-in"
            >
                <span className="flex items-center space-x-1">
                    <Dot color="success" />
                    {isPopover && <CheckIcon />}
                    {isPopover && <span>Checked in</span>}
                </span>

                <span className="flex items-center space-x-1">
                    <NoTranslate>{checkInStatus[GuestStatus.ARRIVED]}</NoTranslate>
                    <span>{isExpanded && "Checked in"}</span>
                </span>
            </li>
            {checkInStatus[GuestStatus.PENDING] > 0 && (
                <li
                    className={clsx("flex items-center space-x-1", isPopover && "justify-between")}
                    data-testid="check-in-status-pending"
                >
                    <span className="flex items-center space-x-1">
                        <Dot color="warning" />
                        {isPopover && <UserChangedIcon />}
                        {isPopover && <span>Pending</span>}
                    </span>

                    <span className="flex items-center space-x-1">
                        <NoTranslate>{checkInStatus[GuestStatus.PENDING]}</NoTranslate>
                        <span>{isExpanded && "Pending"}</span>
                    </span>
                </li>
            )}
            {checkInStatus[GuestStatus.NO_SHOW] > 0 && (
                <li
                    className={clsx("flex items-center space-x-1", isPopover && "justify-between")}
                    data-testid="check-in-status-no-show"
                >
                    <span className="flex items-center space-x-1">
                        <Dot color="danger" />
                        {isPopover && <CircleSubtractIcon />}
                        {isPopover && <span>No Show</span>}
                    </span>

                    <span className="flex items-center space-x-1">
                        <NoTranslate>{checkInStatus[GuestStatus.NO_SHOW]}</NoTranslate>
                        <span>{isExpanded && "No Show"}</span>
                    </span>
                </li>
            )}
            {checkInStatus[GuestStatus.RETURNED] > 0 && (
                <li
                    className={clsx("flex items-center space-x-1", isPopover && "justify-between")}
                    data-testid="check-in-status-returned"
                >
                    <span className="flex items-center space-x-1">
                        <Dot color="success" />
                        {isPopover && <EquipmentIcon />}
                        {isPopover && <span>Returned</span>}
                    </span>

                    <span className="flex items-center space-x-1">
                        <NoTranslate>{checkInStatus[GuestStatus.RETURNED]}</NoTranslate>
                        <span>{isExpanded && "Returned"}</span>
                    </span>
                </li>
            )}
        </ul>
    );
};

interface CheckInStatusPopoverSummaryProps {
    groupedGuestsData: GuestSummaryByStatus;
}

export const CheckInStatusPopoverSummary = ({ groupedGuestsData }: CheckInStatusPopoverSummaryProps) => {
    const sections = [
        {
            status: GuestStatus.ARRIVED,
            label: "Checked in",
            color: "success",
        },
        {
            status: GuestStatus.PENDING,
            label: "Pending",
            color: "warning",
        },
        {
            status: GuestStatus.NO_SHOW,
            label: "No Show",
            color: "danger",
        },
        {
            status: GuestStatus.RETURNED,
            label: "Returned",
            color: "success",
        },
    ];

    return (
        <div className="flex space-x-2 p-2 text-sm">
            {sections.map(({ status, label, color }) => {
                const guests = groupedGuestsData?.[status] ?? [];
                if (guests.length === 0) {
                    return null;
                }

                let total = 0;
                for (const guest of guests) {
                    total += guest.quantity;
                }

                return (
                    <div key={status}>
                        <div className="mb-2 flex items-center space-x-1 whitespace-nowrap font-bold">
                            <Dot color={color} />
                            <span>
                                {total} {label}
                            </span>
                        </div>
                        <ul className="text-gray-700 space-y-1">
                            {guests.map((guest) => (
                                <li key={`${guest.name}_${guest.quantity}`} className="flex justify-between">
                                    <span className="ml-3 truncate">
                                        {guest.quantity} <NoTranslate>{guest.name}</NoTranslate>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>
    );
};
